import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface ValueColumn {
  key: string;
  label: string;
}

/**
 * The table twin every chart on this surface owes its reader.
 *
 * A chart is a shape; some questions are about a number, and some readers
 * cannot use the shape at all. Collapsed by default because the chart is the
 * headline — but present on every block, not just the ones where it was
 * convenient.
 *
 * A plain `overflow-auto` div rather than the shared ScrollArea: that component
 * has silently failed to scroll while also letting content overflow its parent,
 * and a table of a few hundred rows does not need a custom scrollbar.
 */
export function ShowValues({
  columns,
  rows,
  label = 'Show values',
}: {
  columns: ValueColumn[];
  rows: Record<string, string>[];
  label?: string;
}) {
  const [open, setOpen] = useState(false);

  if (rows.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => { setOpen((previous) => !previous); }}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {label} ({rows.length})
      </button>

      {open ? (
        <div className="mt-2 max-h-64 overflow-auto rounded-md border border-border-subtle">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b border-border-subtle">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="text-left font-medium text-muted-foreground px-2 py-1.5 whitespace-nowrap"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {/* Keyed by position: rows are a pure function of the series and
                  are rebuilt wholesale, so there is no identity to preserve
                  across a re-render that reordering could break. */}
              {rows.map((row, index) => (
                <tr key={index}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="px-2 py-1 whitespace-nowrap tabular-nums text-foreground"
                    >
                      {row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
